import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/button';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 text-center space-y-4">
                    <div className="space-y-2">
                        <h2 className="text-2xl font-bold tracking-tight">Something went wrong</h2>
                        <p className="text-muted-foreground max-w-[500px]">
                            We apologize, but an unexpected error occurred. Please try reloading the page.
                        </p>
                        {import.meta.env.DEV && this.state.error && (
                            <pre className="mt-4 p-4 bg-red-50 text-red-900 text-xs text-left overflow-auto rounded max-w-full">
                                {this.state.error.toString()}
                            </pre>
                        )}
                    </div>
                    <Button
                        onClick={() => window.location.reload()}
                        variant="default"
                    >
                        Reload Page
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}
